'use client';

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

/**
 * Angle/render image sizing — a faithful port of the Apple app's rules
 * (AngleImageDisplay.swift / AngleViewerSection.swift / ConceptContentView.swift /
 * ConceptPageLayout.swift). Keep the two platforms in lockstep: any change to a
 * clamp, cap, or floor must be mirrored on Apple/Android.
 *
 * The container is shaped to the ANGLE aspect (clamped 3:4..16:9) and bounded by
 * BOTH the column width and the height cap — `.aspectRatio(_, .fit)` — so it
 * always preserves the image proportions and is centered (side gray strips when
 * narrower than the column). The image fills that exactly-shaped box, so the
 * whole image is always visible — nothing is ever cropped by the container.
 */

/** Aspect-ratio clamp bounds — Apple `min(16/9, max(3/4, ratio))`. */
export const ASPECT_MIN = 3 / 4; // 0.75  — portrait floor
export const ASPECT_MAX = 16 / 9; // 1.778 — landscape ceiling

/**
 * Height-cap floor — the max image height never drops below this even on a very
 * short browser window (the page scrolls instead). This only floors the CAP, so
 * a portrait image can still use the full 360px; it must NOT be applied as a
 * minHeight on the block, or landscape (16:9) images get letterboxed with gray
 * bands top/bottom. The container hugs the image box, exactly like Apple's
 * `.aspectRatio(_, .fit)` — which has no minimum.
 */
const MIN_BLOCK_HEIGHT = 360;

/**
 * Clamp a raw width/height ratio to the 3:4..16:9 range. Falls back to 16:9
 * when no ratio is known yet, matching Apple's `effectiveAspectRatio` default.
 */
export function clampAspectRatio(ratio: number | undefined | null): number {
  if (!ratio || ratio <= 0) return ASPECT_MAX;
  return Math.max(ASPECT_MIN, Math.min(ASPECT_MAX, ratio));
}

export interface ImageSizingConstraints {
  /** Max image height in px — Apple's reserve-based cap. */
  maxImageHeight: number;
  /** True when the image is stacked above the settings (no inspector column). */
  isSingleColumn: boolean;
  /** Whether the client-side measurement has run (false during SSR). */
  isReady: boolean;
}

interface UseImageConstraintsOptions {
  /** True when the concept editor shows the two-column (inspector) layout. */
  isTwoColumn?: boolean;
}

/**
 * Per-layout image height cap from the viewport alone:
 *
 * - Single-column (stacked): `max(viewportH * 0.5, viewportH - 300)`, so the
 *   canvas is as wide as the column allows and the settings still peek below it. This is the real rule there: the
 *   image is the top of a long scroll, it is not fitting anything.
 * - Two-column (desktop inspector): a rough guess, superseded by the measured
 *   column fit that `AngleImageFrame` publishes (see {@link fittedImageHeightCap}).
 *   Kept for the first paint and for SSR, where nothing is measurable yet.
 *
 * Both are floored at {@link MIN_BLOCK_HEIGHT} so the block never gets too short.
 */
export function useImageConstraints(
  options?: UseImageConstraintsOptions
): ImageSizingConstraints {
  const isTwoColumn = options?.isTwoColumn ?? false;

  const [state, setState] = useState<ImageSizingConstraints>(() =>
    computeCap(800, isTwoColumn, false)
  );

  useEffect(() => {
    const calculate = () => {
      setState(computeCap(window.innerHeight, isTwoColumn, true));
    };

    calculate();

    window.addEventListener('resize', calculate);
    window.addEventListener('orientationchange', calculate);

    return () => {
      window.removeEventListener('resize', calculate);
      window.removeEventListener('orientationchange', calculate);
    };
  }, [isTwoColumn]);

  return state;
}

/**
 * Breathing room kept free BELOW the image inside the content column, so the
 * top of whatever follows (the concept note, the LLM feedback banners) is
 * visible without scrolling and the user knows there is more. Apple's
 * `ConceptContentView` reserve, same numbers.
 */
export function bottomReserve(columnHeight: number): number {
  return Math.min(columnHeight * 0.06, 60);
}

/**
 * Height cap for an image that has to FIT its content column: the column's own
 * height minus the chrome above it, the gap below it and {@link bottomReserve}.
 *
 * All three inputs are independent of the image height — the column is sized by
 * the page shell, the chrome above is whatever the tabs/header need — so this
 * never feeds back into itself. `AngleImageFrame` measures them; the viewport
 * heuristic below is only the pre-measurement fallback.
 */
export function fittedImageHeightCap(
  columnHeight: number,
  chromeAbove: number,
  gapBelow: number
): number {
  return Math.max(
    MIN_BLOCK_HEIGHT,
    columnHeight - chromeAbove - gapBelow - bottomReserve(columnHeight)
  );
}

function computeCap(
  viewportHeight: number,
  isTwoColumn: boolean,
  isReady: boolean
): ImageSizingConstraints {
  let maxImageHeight: number;
  if (isTwoColumn) {
    // Pre-measurement fallback only — `AngleImageFrame` replaces this with the
    // measured column fit as soon as it has laid out. The static 100px stands
    // for the page chrome above the image (topbar + angle tabs + gaps); it is a
    // guess, which is exactly why the frame measures the real thing.
    const reserve = bottomReserve(viewportHeight);
    maxImageHeight = viewportHeight - 100 - reserve;
  } else {
    // The stacked canvas takes the full column width whenever the height
    // allows: the cap leaves ~300px for the page chrome above it (header,
    // picker, angle tabs) and the top edge of the settings below, which must
    // stay visible. Never less than half the viewport (the old rule), so a
    // short window keeps the settings peeking too.
    maxImageHeight = Math.max(viewportHeight * 0.5, viewportHeight - 300);
  }

  return {
    maxImageHeight: Math.max(MIN_BLOCK_HEIGHT, maxImageHeight),
    isSingleColumn: !isTwoColumn,
    isReady,
  };
}

/**
 * Widest the image box can ever get: the height cap turned into a width by the
 * image ratio. Beyond this the box is height-bound and leaves side strips in the
 * card frame — the same predicate Apple uses in
 * `AngleViewerSection.angleCardNeedsVerticalPadding` (`aspect < cardW / maxH`).
 */
export function imageBoxMaxWidth(
  aspectRatio: number | null | undefined,
  constraints: ImageSizingConstraints
): number {
  return constraints.maxImageHeight * clampAspectRatio(aspectRatio);
}

/**
 * Build the exactly-shaped container box from a clamped aspect ratio and the
 * sizing constraints — the CSS equivalent of Apple's
 * `.aspectRatio(aspect, .fit).frame(maxHeight:).frame(maxWidth:.infinity)`.
 *
 * The box preserves the image ratio and is bounded by BOTH the column width and
 * the height cap (whichever binds first), then centered. It is NEVER stretched
 * away from the ratio, so `object-cover` inside it fills exactly and the whole
 * image stays visible — the container crops nothing.
 *
 * NOTHING here is measured. The box is `width: min(100%, capWidth)` with an
 * `aspect-ratio`, so the browser resolves both dimensions in its own layout
 * pass: the column width enters as a percentage, never as a number that JS read
 * back and fed into a style. That is deliberate and load-bearing — a measured
 * width that decides a height is a feedback loop (image height → column
 * overflow → scrollbar → column width → image height) that strobes at the
 * viewport sizes where it balances. See `AngleImageFrame` for the sibling rule
 * and `ProjectDesktopLayout` for the `scrollbar-gutter` that keeps the column
 * width independent of overflow. Do not reintroduce a ResizeObserver here.
 */
export function computeImageContainerStyle(
  aspectRatio: number,
  constraints: ImageSizingConstraints
): CSSProperties {
  return {
    width: `min(100%, ${imageBoxMaxWidth(aspectRatio, constraints)}px)`,
    aspectRatio: aspectRatio.toString(),
    marginInline: 'auto',
  };
}

/** Card-frame strip cap (px): the frame never pads more than this per side. */
export const IMAGE_FRAME_STRIP_PADDING = 8;

/**
 * Vertical padding for the card frame around a sized image box, as a CSS value:
 * it matches the side strips the centered box leaves, capped at 8px, and is 0
 * when the box fills the frame width. Percentage padding resolves against the
 * frame's own inline size, so the browser evaluates the whole rule during
 * layout — no measurement, no state, no threshold to oscillate around.
 */
export function imageFrameStripPadding(
  imageBoxWidth: number | null | undefined
): string | undefined {
  if (!imageBoxWidth || imageBoxWidth <= 0) return undefined;
  return `clamp(0px, calc((100% - ${imageBoxWidth}px) / 2), ${IMAGE_FRAME_STRIP_PADDING}px)`;
}
